import { type DataSource, MethodType, type TableSeries } from '@piccolohealth/pbs-common';
import { ChakraV3, FloatingPopover } from '@piccolohealth/ui';
import { uuid, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { FormSection } from '../../../../components/forms/FormSection';
import { HookedSubmitButton } from '../../../../components/forms/V3HookedSubmitButton';
import { zodResolver } from '../../../../utils/zod';
import type { UseTableReturn } from '../../hooks/useTable';
import { METHOD_TYPE_OPTIONS } from '../../utils';
import { TableSeriesForm } from './TableSeriesForm';

type FormValues = TableSeries;

const sourceSchema: z.ZodSchema<DataSource> = z.object({
  __typename: z.literal('ParticipantBehaviourDataSource'),
  method: z.enum(MethodType),
  participantBehaviourId: z.string(),
});

export const tableSeriesSchema: z.ZodSchema<TableSeries> = z.object({
  __typename: z.literal('TableSeries'),
  id: z.string(),
  source: sourceSchema,
});

interface TimeSeriesAddFormProps {
  participantBehaviourId: string;
  table: UseTableReturn;
  onClose: () => void;
}

export const TableSeriesAddForm = (props: TimeSeriesAddFormProps) => {
  const { participantBehaviourId, table, onClose } = props;

  const initialValues: TableSeries = {
    __typename: 'TableSeries',
    id: uuid(),
    source: {
      __typename: 'ParticipantBehaviourDataSource',
      method: METHOD_TYPE_OPTIONS[0]!.raw,
      participantBehaviourId,
    },
  };

  const methods = useForm<TableSeries>({
    defaultValues: initialValues,
    resolver: zodResolver(tableSeriesSchema),
  });

  const { reset } = methods;

  const onSubmit = (values: FormValues) => {
    table.onAddSeries(values);
    reset({
      ...initialValues,
      id: uuid(),
    });
    onClose();
  };

  return (
    <ChakraV3.Stack
      bg='white'
      gap={0}
      w='lg'
      layerStyle='bordered'
      shadow='2xl'
      rounded='md'
      overflow='hidden'
      p={4}
    >
      <FormProvider {...methods}>
        <FormSection as='form' heading='Add series' onSubmit={methods.handleSubmit(onSubmit)}>
          <ChakraV3.Separator />
          <TableSeriesForm table={table} />
          <ChakraV3.Separator />
          <HookedSubmitButton w='full' size='sm' colorPalette='brand' disabled={false}>
            Add
          </HookedSubmitButton>
        </FormSection>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

interface Props {
  participantBehaviourId: string;
  table: UseTableReturn;
}

export const TableSeriesAddControl = (props: Props) => {
  const { participantBehaviourId, table } = props;

  const { open, onOpen, onClose } = ChakraV3.useDisclosure();

  return (
    <FloatingPopover
      open={open}
      setOpen={(v) => (v ? onOpen() : onClose())}
      render={() => (
        <TableSeriesAddForm
          participantBehaviourId={participantBehaviourId}
          table={table}
          onClose={onClose}
        />
      )}
    >
      <ChakraV3.Button w='full' variant='outline' borderStyle='dashed' size='sm' onClick={onOpen}>
        Add series
        <FaPlus />
      </ChakraV3.Button>
    </FloatingPopover>
  );
};
